import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../services/axiosInstance";
import AuthPasswordField from "./AuthPasswordField";
import "./SignInForm.css";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ password: "", confirm: "", form: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setErrors((prev) => ({
        ...prev,
        form: "Invalid or missing token. Please use the link from your email.",
      }));
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({ password: "", confirm: "", form: "" });
    setMessage("");

    if (newPassword.length < 8) {
      setErrors((prev) => ({ ...prev, password: "Minimum 8 characters required." }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirm: "Passwords do not match." }));
      return;
    }

    setIsLoading(true);
    try {
      await api.post(`/auth/reset-password?token=${token}`, {
        password: newPassword,
      });
      setMessage("Password successfully reset. Redirecting to sign in…");
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        form: err.response?.data?.message || "Something went wrong.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#F2EADB" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="login-glass-card relative w-full max-w-md rounded-2xl p-8"
      >
        <div className="flex flex-col items-center mb-6 text-center">
          <h2 className="text-xl font-bold text-[#0B3B2E] tracking-tight">
            Reset your password
          </h2>
          <p className="text-sm text-[#5A6B65] mt-1">
            Choose a new password for your Renewable Operations workspace
          </p>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="space-y-5"
        >
          <AuthPasswordField
            label="New password"
            placeholder="New password"
            value={newPassword}
            onChange={setNewPassword}
            show={showPassword}
            toggleShow={() => setShowPassword((s) => !s)}
            error={errors.password}
            disabled={isLoading}
            hint="Your password must be at least 8 characters"
          />

          <AuthPasswordField
            label="Confirm password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showPassword}
            toggleShow={() => setShowPassword((s) => !s)}
            error={errors.confirm}
            disabled={isLoading}
          />

          {errors.form && (
            <p className="text-sm text-error-500 text-center">{errors.form}</p>
          )}
          {message && (
            <p className="text-sm text-[#076333] font-medium text-center">{message}</p>
          )}

          <motion.button
            type="submit"
            disabled={isLoading || !token}
            whileHover={!isLoading ? { scale: 1.015, y: -1 } : undefined}
            whileTap={!isLoading ? { scale: 0.985 } : undefined}
            className="login-glass-btn relative w-full h-14 rounded-xl text-white disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="relative flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Resetting…
                </>
              ) : (
                "Reset password"
              )}
            </span>
          </motion.button>
        </motion.form>
      </motion.div>
    </div>
  );
}
